import React, { ReactElement, useEffect, useState } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { App } from 'common/components/antd/App';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { StatusText } from 'common/components/StatusText/StatusText';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { notifySuccess } from 'common/utils/notifier.utils';
import { useCreateEmployeeMutation, useUpdateEmployeeMutation } from 'domains/employees/queries/employees.query';
import { useGetCounterpartyRoleListQuery } from 'domains/roles/queries/roles.queries';
import { TRolesListResData } from 'domains/roles/types/roles.types';
import { SFormContainer, SFooter, SSection, STitle } from './modal.style';
import { EEmployeesFormItemName, EModalType } from '../enums/employees.enum';
import { TEmployeesModalData } from '../types/employees.types';

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TEmployeesModalData;
};

const CreateEditEmployeeModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    data: {
      rowData,
      type: modalType,
      selectedCounterparty,
      branchesData,
      counterpartiesData,
    },
  } = props;

  const { notification } = App.useApp();

  const { t } = useTranslation();

  const [form] = Form.useForm();

  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

  const [selectedCounterpartyGuid, setSelectedCounterpartyGuid] = useState<string>(ENV_COUNTERPARTY_GUID);

  useEffect(() => {
    if (Object.keys(rowData || {}).length) {
      const { name, surname, paternal_name, status, role, email, phone, counterparty, branch_guid } = rowData;

      form.setFields([
        {
          name: EEmployeesFormItemName.Counterparty,
          value: counterparty,
        },
        {
          name: EEmployeesFormItemName.Branch,
          value: branch_guid,
        },
        {
          name: EEmployeesFormItemName.Role,
          value: role,
        },
        {
          name: EEmployeesFormItemName.Status,
          value: status,
        },
        {
          name: EEmployeesFormItemName.Name,
          value: name,
        },
        {
          name: EEmployeesFormItemName.Phone,
          value: phone,
        },
        {
          name: EEmployeesFormItemName.Surname,
          value: surname,
        },
        {
          name: EEmployeesFormItemName.Email,
          value: email,
        },
        {
          name: EEmployeesFormItemName.PaternalName,
          value: paternal_name,
        },
      ]);
    } else {
      form.setFields([
        {
          name: EEmployeesFormItemName.Counterparty,
          value: selectedCounterparty,
        },
      ]);
    }
  }, [rowData]);

  const {
    data: rolesData,
    isError: isRolesListDataError,
    error: rolesListDataError,
    isLoading: isRolesListDataLoading,
  } = useGetCounterpartyRoleListQuery(rowData?.counterparty_guid || selectedCounterpartyGuid, {
    enabled: !!rowData?.counterparty_guid || !!selectedCounterpartyGuid,
  });

  const { mutateAsync: mutateAsyncCreateEmployeeMutation } = useCreateEmployeeMutation();

  const { mutateAsync: mutateAsyncUpdateEmployeeMutation } = useUpdateEmployeeMutation();

  useEffect(() => {
    if (isRolesListDataError) {
      notification.error({
        message: (rolesListDataError as { message: string }).message,
        type: 'error',
      });
    }
  }, [isRolesListDataError, rolesListDataError]);

  const statusOptions = [
    {
      value: 1,
      label: t('usrRoleMng.employees.modal.edit.statusOption.active'),
      color: '#52C41A',
    },
    {
      value: 2,
      label: t('usrRoleMng.employees.modal.edit.statusOption.block'),
      color: '#FF4D4F',
    },
    {
      value: 3,
      label: t('usrRoleMng.employees.modal.edit.statusOption.closed'),
      color: '#D9D9D9',
    },
  ];

  const onChangeFormHandler = (formData: Record<EEmployeesFormItemName, string>): void => {
    const { counterparty } = formData;

    if (counterparty) {
      setSelectedCounterpartyGuid(counterparty);
      form.resetFields();
    }
  };

  const onFinishFormHandler = async (formData: Record<EEmployeesFormItemName, any>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);

      const { counterparty, branch_guid, status, name, phone, surname, email, paternal_name, role } = formData;

      if (modalType === EModalType.Edit) {
        const updateEmployeeReqData = {
          name,
          surname,
          paternal_name,
          email,
          phone,
          branch_guid,
          status,
          login: email,
        };

        await mutateAsyncUpdateEmployeeMutation({
          counterpartyGUID: rowData.counterparty_guid,
          employeeGUID: rowData.employee_guid,
          requestData: updateEmployeeReqData,
        });
        notifySuccess('usrRoleMng.employees.modal.update.notification.success');
      } else {
        const createEmployeeReqData = {
          name,
          surname,
          paternal_name,
          email,
          phone,
          ...(branch_guid?.length ? { branch_guid } : null),
          login: email,
          roles: role,
        };

        await mutateAsyncCreateEmployeeMutation({
          counterpartyGUID: counterparty,
          requestData: createEmployeeReqData,
        });
        notifySuccess('usrRoleMng.employees.modal.create.notification.success');
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.EmployeesQueryKeys].getEmployees_MultiRole._def,
      });

      setIsSubmitBtnLoading(false);
      onCancelModalHandler();
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  const onCloseModalHandler = (): void => {
    form.resetFields();

    onCancelModalHandler();
  };

  useEffect(() => {
    form.setFieldValue(EEmployeesFormItemName.Counterparty, ENV_COUNTERPARTY_GUID);
  }, []);

  return (
    <EnhancedModal open={isOpen} width={764} onCancel={onCloseModalHandler}>
      <Form form={form} onFinish={onFinishFormHandler} onValuesChange={onChangeFormHandler}>
        <Header>
          {modalType === EModalType.Edit
            ? t('usrRoleMng.employees.modal.edit.title')
            : t('usrRoleMng.employees.modal.create.title')}
        </Header>

        <SSection>
          <STitle>{t('usrRoleMng.employees.modal.edit.section.title.user')}</STitle>
        </SSection>

        <SFormContainer>
          <Form.Item
            name={EEmployeesFormItemName.Counterparty}
            label={t('usrRoleMng.employees.modal.edit.label.counterparty')}
          >
            <Select
              size="large"
              disabled
              placeholder={t('usrRoleMng.employees.modal.edit.placeholder.counterparty')}
            >
              {(counterpartiesData?.counterparties || []).map((option) => (
                <Select.Option key={option.guid} value={option.guid}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t('usrRoleMng.employees.modal.edit.label.branchName')}
            name={EEmployeesFormItemName.Branch}
          >
            <Select
              size="large"
              placeholder={t('usrRoleMng.employees.modal.edit.placeholder.branchName')}
              allowClear
            >
              {(branchesData?.branches || []).map((option) => (
                <Select.Option key={option?.guid} value={option.guid}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={EEmployeesFormItemName.Role} label={t('usrRoleMng.employees.modal.edit.label.role')}>
            <Select
              mode="multiple"
              optionLabelProp="label"
              size="large"
              placeholder={t('usrRoleMng.employees.modal.edit.placeholder.role')}
              loading={isRolesListDataLoading}
            >
              {((rolesData as TRolesListResData)?.roles || []).map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={EEmployeesFormItemName.Status} label={t('usrRoleMng.employees.modal.edit.label.status')}>
            <Select size="large" placeholder={t('usrRoleMng.employees.modal.edit.placeholder.status')}>
              {statusOptions.map(({ label, value, color }) => (
                <Select.Option key={value} value={label}>
                  <StatusText color={color}>{label}</StatusText>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </SFormContainer>

        <SSection>
          <STitle>{t('usrRoleMng.employees.modal.edit.section.title.personalInfo')}</STitle>
        </SSection>

        <SFormContainer>
          <Form.Item name={EEmployeesFormItemName.Name} label={t('usrRoleMng.employees.modal.edit.label.name')}>
            <Input
              placeholder={t('usrRoleMng.employees.modal.edit.placeholder.name') as string}
              size="large"
            />
          </Form.Item>
          <Form.Item name={EEmployeesFormItemName.Phone} label={t('usrRoleMng.employees.modal.edit.label.phoneNumber')}>
            <Input
              type="tel"
              size="large"
              placeholder={t('usrRoleMng.employees.modal.edit.placeholder.phoneNumber') as string}
            />
          </Form.Item>
          <Form.Item name={EEmployeesFormItemName.Surname} label={t('usrRoleMng.employees.modal.edit.label.surname')}>
            <Input size="large" placeholder={t('usrRoleMng.employees.modal.edit.placeholder.surname') as string} />
          </Form.Item>
          <Form.Item name={EEmployeesFormItemName.Email} label={t('usrRoleMng.employees.modal.edit.label.email')}>
            <Input
              size="large"
              type="email"
              disabled={modalType !== EModalType.Create}
              placeholder={t('usrRoleMng.employees.modal.edit.placeholder.email') as string}
            />
          </Form.Item>
          <Form.Item
            name={EEmployeesFormItemName.PaternalName}
            label={t('usrRoleMng.employees.modal.edit.label.paternalName')}
          >
            <Input size="large" placeholder={t('usrRoleMng.employees.modal.edit.placeholder.paternalName') as string} />
          </Form.Item>
        </SFormContainer>

        <Footer>
          <SFooter>
            <Button
              style={{
                marginRight: 12,
              }}
              onClick={onCloseModalHandler}
            >
              {t('usrRoleMng.employees.modal.edit.button.cancel')}
            </Button>
            <Button loading={isSubmitBtnLoading} htmlType="submit" type="primary">
              {modalType === EModalType.Edit
                ? t('usrRoleMng.employees.modal.edit.button.create')
                : t('usrRoleMng.employees.modal.create.button.create')}
            </Button>
          </SFooter>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default CreateEditEmployeeModal;
